import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { TrendUp, TrendDown, ShieldCheck, Warning, ChartBar, Eye, ChalkboardTeacher, ClipboardText } from "@phosphor-icons/react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip as ChartTooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell, CartesianGrid } from "recharts";
import NomSemaphore from "./NomSemaphore";

const Kpi = ({ label, value, unit = "", color = "text-slate-900", icon: Icon, testid, hint }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-slate-500 text-[11px] uppercase tracking-widest font-bold">
      {Icon && <Icon size={14} weight="duotone" className="text-blue-900" />} {label}
    </div>
    <p className={`font-heading font-black text-3xl tracking-tight mt-1 ${color}`}>
      {value}
      {unit && <span className="text-sm font-normal text-slate-500 ml-1">{unit}</span>}
    </p>
    {hint && <p className="text-[11px] text-slate-400 mt-0.5">{hint}</p>}
  </div>
);

const SEV_COLORS = { low: "#10B981", medium: "#F59E0B", high: "#DC2626" };
const IPER_COLORS = { bajo: "#10B981", medio: "#F59E0B", alto: "#EA580C", critico: "#DC2626" };

export default function HseDashboard() {
  const [kpis, setKpis] = useState(null);
  const [stats, setStats] = useState(null);
  const [period, setPeriod] = useState(90);

  const load = useCallback(() => {
    api.get("/hse/kpis").then((r) => setKpis(r.data)).catch(() => {});
    api.get(`/hse/stats?days=${period}`).then((r) => setStats(r.data)).catch(() => {});
  }, [period]);
  useEffect(() => { load(); }, [load]);

  const iperInitialData = useMemo(() => (
    stats ? Object.entries(stats.iper_initial).map(([k, v]) => ({ name: k, value: v, fill: IPER_COLORS[k] })) : []
  ), [stats]);
  const iperResidualData = useMemo(() => (
    stats ? Object.entries(stats.iper_residual).map(([k, v]) => ({ name: k, value: v, fill: IPER_COLORS[k] })) : []
  ), [stats]);
  const sevData = useMemo(() => (
    stats ? stats.obs_by_severity.map((s) => ({ ...s, fill: SEV_COLORS[s.severity] || "#94A3B8" })) : []
  ), [stats]);

  if (!kpis) return <p className="text-sm text-slate-400 text-center py-16">Cargando KPIs…</p>;
  const l = kpis.lagging;
  const g = kpis.leading;

  return (
    <div className="space-y-6">
      <section>
        <div className="flex items-baseline gap-2 mb-3">
          <p className="font-heading font-bold text-slate-900">Indicadores reactivos (Lagging)</p>
          <span className="text-xs text-slate-500">— base {kpis.man_hours.toLocaleString()} HH del período</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="TRIR" value={l.trir} testid="hse-kpi-trir" hint="Total Recordable Incident Rate" icon={TrendDown} color={l.trir > 2 ? "text-red-600" : "text-emerald-700"} />
          <Kpi label="LTIFR" value={l.ltifr} testid="hse-kpi-ltifr" hint="Lost Time Injury Frequency Rate" icon={TrendDown} color={l.ltifr > 1 ? "text-red-600" : "text-emerald-700"} />
          <Kpi label="Índice de Frecuencia (IF)" value={l.if_rate} testid="hse-kpi-if" hint="Mexicano · × 10⁶ HH" />
          <Kpi label="Índice de Severidad (IS)" value={l.is_rate} testid="hse-kpi-is" hint="Días perdidos × 10⁶ HH" />
          <Kpi label="Días perdidos" value={l.days_lost} testid="hse-kpi-days-lost" icon={Warning} color="text-red-700" />
          <Kpi label="Incidentes registrables" value={l.recordable} testid="hse-kpi-recordable" />
          <Kpi label="Lesiones con tiempo perdido" value={l.lti} testid="hse-kpi-lti" />
          <Kpi label="Total de incidentes" value={l.total_incidents} testid="hse-kpi-total" />
        </div>
      </section>

      <NomSemaphore year={new Date().getFullYear()} />

      <section data-testid="hse-ocas-target-section">
        <div className="flex items-baseline gap-2 mb-3">
          <p className="font-heading font-bold text-slate-900">OCAS · Cumplimiento del mes</p>
          <span className="text-xs text-slate-500">— meta configurable en la pestaña "Objetivos"</span>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="hse-ocas-compliance">
          {g.observations_month_target > 0 ? (
            <>
              <div className="flex items-baseline justify-between mb-1">
                <div>
                  <p className="text-xs uppercase tracking-widest font-bold text-slate-500">
                    OCAS registradas · {g.observations_month_ym}
                  </p>
                  <p className="font-heading font-black text-3xl mt-1">
                    <span data-testid="hse-ocas-month-count">{g.observations_month_count}</span>
                    <span className="text-slate-400 text-base font-normal"> / {g.observations_month_target} meta</span>
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs uppercase tracking-widest font-bold text-slate-500">Cumplimiento</p>
                  <p
                    data-testid="hse-ocas-compliance-pct"
                    className={`font-heading font-black text-3xl ${(g.observations_month_compliance || 0) >= 100 ? "text-emerald-600" : (g.observations_month_compliance || 0) >= 70 ? "text-amber-600" : "text-red-600"}`}
                  >
                    {g.observations_month_compliance ?? "—"}<span className="text-base font-normal text-slate-500 ml-0.5">%</span>
                  </p>
                </div>
              </div>
              <div className="mt-2 h-3 w-full rounded-full bg-slate-100 overflow-hidden">
                <div
                  className={`h-full transition-all ${(g.observations_month_compliance || 0) >= 100 ? "bg-emerald-500" : (g.observations_month_compliance || 0) >= 70 ? "bg-amber-500" : "bg-red-500"}`}
                  style={{ width: `${Math.min(g.observations_month_compliance || 0, 100)}%` }}
                  data-testid="hse-ocas-progress-bar"
                />
              </div>
              <p className="text-[10px] text-slate-500 italic mt-2">
                Meta configurada en <b>Objetivos → Observaciones/mes (mínimo)</b>. El PDF general SGSSO ya la reporta con ✓/✗.
              </p>
            </>
          ) : (
            <p className="text-xs text-slate-500 italic">
              Sin meta de OCAS/mes configurada. Ve a la pestaña <b>Objetivos</b> y define un valor en <b>Observaciones/mes (mínimo)</b>.
            </p>
          )}
        </div>
      </section>

      <section>
        <div className="flex items-baseline gap-2 mb-3">
          <p className="font-heading font-bold text-slate-900">Indicadores proactivos (Leading)</p>
          <span className="text-xs text-slate-500">— cultura y prevención</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Observaciones (30d)" value={g.observations_30d} icon={Eye} testid="hse-kpi-observations-30d" color="text-emerald-700" />
          <Kpi label="Charlas 5-min (30d)" value={g.toolbox_30d} icon={ChalkboardTeacher} testid="hse-kpi-toolbox-30d" color="text-blue-900" />
          <Kpi label="Inspecciones (30d)" value={g.inspections_30d} icon={ClipboardText} testid="hse-kpi-inspections-30d" />
          <Kpi label="IPER altos/críticos" value={`${g.iper_high_or_critical}/${g.iper_total}`} icon={ShieldCheck} testid="hse-kpi-iper" color="text-orange-700" />
          <Kpi label="Acciones abiertas" value={g.actions_open} icon={ChartBar} testid="hse-kpi-actions-open" color={g.actions_overdue > 0 ? "text-red-600" : "text-slate-900"} />
          <Kpi label="Acciones vencidas" value={g.actions_overdue} icon={Warning} testid="hse-kpi-actions-overdue" color={g.actions_overdue > 0 ? "text-red-600" : "text-slate-900"} />
          <Kpi label="% cierre a tiempo" value={g.actions_ontime_pct} unit="%" testid="hse-kpi-actions-ontime" color={g.actions_ontime_pct >= 80 ? "text-emerald-700" : "text-amber-700"} />
          <Kpi label="Total acciones" value={g.actions_total} testid="hse-kpi-actions-total" />
        </div>
      </section>

      {/* Fase 2 — Analíticos avanzados */}
      <section data-testid="hse-stats-section">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="font-heading font-bold text-slate-900">Analíticos avanzados</p>
            <span className="text-xs text-slate-500">Serie temporal, hot-spots y distribución de riesgo</span>
          </div>
          <div className="flex gap-1 bg-slate-100 rounded-md p-1">
            {[30, 90, 180, 365].map((d) => (
              <button
                key={d}
                data-testid={`hse-stats-period-${d}`}
                onClick={() => setPeriod(d)}
                className={`px-2.5 py-1 text-xs font-semibold rounded transition ${period === d ? "bg-white text-blue-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
              >
                {d === 365 ? "1 año" : `${d}d`}
              </button>
            ))}
          </div>
        </div>

        {!stats ? (
          <p className="text-sm text-slate-400 text-center py-8">Cargando analíticos…</p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="hse-stats-weekly">
              <p className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">Actividad semanal</p>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={stats.weekly} margin={{ top: 5, right: 15, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="week" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <ChartTooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="incidents" stroke="#DC2626" name="Incidentes" dot={{ r: 2 }} />
                  <Line type="monotone" dataKey="observations" stroke="#10B981" name="Observaciones" dot={{ r: 2 }} />
                  <Line type="monotone" dataKey="toolbox" stroke="#1E3A8A" name="Charlas 5-min" dot={{ r: 2 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="hse-stats-obs-by-area">
              <p className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">Observaciones por área (top 10)</p>
              {stats.obs_by_area.length === 0 ? (
                <p className="text-xs text-slate-400 py-6 text-center">Sin datos en el período</p>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={stats.obs_by_area} margin={{ top: 5, right: 15, left: -20, bottom: 0 }} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                    <YAxis dataKey="area" type="category" tick={{ fontSize: 10 }} width={100} />
                    <ChartTooltip />
                    <Bar dataKey="count" fill="#F59E0B" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="hse-stats-obs-severity">
              <p className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">Severidad de observaciones</p>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={sevData} dataKey="count" nameKey="severity" cx="50%" cy="50%" outerRadius={70} label={(e) => `${e.severity}: ${e.count}`}>
                    {sevData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                  </Pie>
                  <ChartTooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="hse-stats-iper-levels">
              <p className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">Distribución de niveles IPER</p>
              <div className="grid grid-cols-2 gap-2 h-[220px]">
                <div>
                  <p className="text-[10px] text-slate-500 text-center">Inicial</p>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={iperInitialData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={55}>
                        {iperInitialData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                      </Pie>
                      <ChartTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div>
                  <p className="text-[10px] text-slate-500 text-center">Residual</p>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={iperResidualData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={55}>
                        {iperResidualData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                      </Pie>
                      <ChartTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {stats.overdue_ranking.length > 0 && (
              <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg p-4" data-testid="hse-stats-overdue-ranking">
                <p className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">Ranking de responsables con acciones vencidas</p>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={stats.overdue_ranking} margin={{ top: 5, right: 15, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="responsible" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                    <ChartTooltip />
                    <Bar dataKey="count" fill="#DC2626" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
